import { hasPermission, SOCKET_EVENTS, type ServerStats } from '@hearth/shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ConsolePanel } from '@/components/console/console-panel';
import { addSample, UsageCharts, type Sample } from '@/components/console/usage-charts';
import { EULA_PATTERN, EulaDialog } from '@/components/eula-dialog';
import { ServerStatusAlerts } from '@/components/server-alerts';
import { StatusPill } from '@/components/status-badges';
import { HEARTH_PREFIX } from '@/lib/console/classify';
import { LogStore } from '@/lib/console/log-store';
import { duration } from '@/lib/format';
import { serverQuery } from '@/lib/server-queries';
import { serverSocket, useServerSocket, type Connection } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server/console')({ component: ConsolePage });

function ConsolePage() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const store = useMemo(() => new LogStore(), []);
  const prevStats = useRef<ServerStats | null>(null);
  const [history, setHistory] = useState<Sample[]>([]);
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [state, setState] = useState<ServerStats['state'] | null>(null);
  const [connection, setConnection] = useState<{ c: Connection; error?: string }>({
    c: 'connecting',
  });
  const [eulaOpen, setEulaOpen] = useState(false);
  const eulaShown = useRef(false);
  const hasEula = server.egg.features.includes('eula');
  const suspended = server.status === 'suspended';

  const highlights = server.profile.console.highlights;
  useEffect(() => store.setHighlights(highlights), [store, highlights]);

  const line = (text: string, source: 'console' | 'daemon' = 'console') => {
    store.push(text, source);
    if (hasEula && !eulaShown.current && EULA_PATTERN.test(text)) {
      eulaShown.current = true;
      setEulaOpen(true);
    }
  };

  const socket = useServerSocket(suspended ? null : id, {
    onReady: () => {
      // Fresh history after every (re)connect, so nothing is duplicated or missed.
      store.clear();
      eulaShown.current = false;
      serverSocket(id).send(SOCKET_EVENTS.sendLogs);
      setConnection({ c: 'connected' });
    },
    onConsole: line,
    onDaemonMessage: (m) => line(HEARTH_PREFIX + m, 'daemon'),
    onInstall: (l) => store.push(l, 'install'),
    onDaemonError: (m) => {
      store.push(HEARTH_PREFIX + m, 'daemon-error');
      toast.error(m);
    },
    onStatus: (st) => {
      setState(st);
      if (st === 'starting') eulaShown.current = false;
    },
    onStats: (s) => {
      setStats(s);
      setState(s.state);
      if (s.state === 'offline') {
        prevStats.current = null;
        return;
      }
      setHistory((h) => addSample(h, prevStats.current, s, Date.now()));
      prevStats.current = s;
    },
    onConnection: (c, error) => setConnection({ c, error }),
  });
  // The socket may already be connected (shared with the layout); onReady covers that too.
  const liveState = state ?? socket?.state ?? null;
  const canConsole = hasPermission(server.permissions, 'control.console');
  const running = liveState === 'running' || liveState === 'starting';

  const send = (cmd: string) => {
    if (!socket?.send(SOCKET_EVENTS.sendCommand, cmd)) {
      toast.error('Not connected to the node.');
      return false;
    }
    return true;
  };

  return (
    <div className="grid gap-4">
      <ServerStatusAlerts server={server} connection={connection} />

      <ConsolePanel
        store={store}
        server={id}
        serverName={server.name}
        commands={server.profile.console.commands}
        connection={<ConnectionIndicator connection={connection.c} suspended={suspended} />}
        uptime={running && stats ? duration(stats.uptimeMs) : null}
        commandDisabled={!canConsole || !running || connection.c !== 'connected'}
        commandPlaceholder={
          !canConsole
            ? 'You can’t send commands to this server'
            : running
              ? 'Type a command…'
              : 'Start the server to send commands'
        }
        onCommand={send}
      />

      <UsageCharts
        history={history}
        cpuLimit={server.limits.cpuPercent}
        memoryLimitBytes={server.limits.memoryMb * 1024 ** 2}
        running={running}
      />

      {hasEula && (
        <EulaDialog
          server={id}
          open={eulaOpen}
          onOpenChange={setEulaOpen}
          onAccepted={() => socket?.send(SOCKET_EVENTS.setState, 'start')}
        />
      )}
    </div>
  );
}

function ConnectionIndicator({
  connection,
  suspended,
}: {
  connection: Connection;
  suspended: boolean;
}) {
  if (suspended) return null;
  const view =
    connection === 'connected'
      ? { tone: 'success', label: 'Live' }
      : connection === 'error'
        ? { tone: 'destructive', label: 'Disconnected' }
        : { tone: 'warning', label: connection === 'reconnecting' ? 'Reconnecting' : 'Connecting' };
  return (
    <StatusPill
      tone={view.tone as 'success' | 'destructive' | 'warning'}
      pulse={connection === 'connecting' || connection === 'reconnecting'}
      aria-live="polite"
    >
      {view.label}
    </StatusPill>
  );
}
